/**
 * Column sorting with a per-column default direction.
 *
 * Descending-first on every numeric measure puts the registers with the most outstanding work at
 * the top, which is the product rule behind the prototype's one special case on `name`. The sort
 * itself is a server query; this hook only decides what to ask for.
 */

import { useCallback } from 'react';

export interface SortState<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

export function useTableSort<K extends string>(
  sort: SortState<K>,
  onChange: (next: SortState<K>) => void,
  defaultDirection: Partial<Record<K, 'asc' | 'desc'>> = {},
): (key: K) => void {
  return useCallback(
    (key: K) => {
      if (key === sort.key) {
        onChange({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
        return;
      }
      onChange({ key, dir: defaultDirection[key] ?? 'desc' });
    },
    [sort.key, sort.dir, onChange, defaultDirection],
  );
}
